import React from 'react';
import { AnimatePresence } from 'motion/react';
import { Select } from '@oysterjs/ui/select';
import { Textarea } from '@oysterjs/ui/textarea';
import {
  TriggerEmailPrivacy as Privacy,
  CustomPresetState,
  type CustomPresetFieldsFragment,
  type TriggerTargetInput
} from '~/api/graphql';
import { type EmailErrors, type EmailOptions } from '~/api/data/triggers/email-config';
import { Description, ErrorMessage, Field, Label } from '@oysterjs/ui/fieldset';
import { Badge } from '@oysterjs/ui/badge';
import { SegmentedControl, SegmentedControlOption } from '@oysterjs/ui/segmented-control';
import { CustomPresetChip, UseCaseChip } from '~/components/home/use-case-tile';
import { TaskSelection } from '~/pages/workflows/trigger-task-picker';
import { BuilderField, Help } from '~/components/triggers/email-builder/fields';
import { UseCaseBrowser } from '~/components/home/use-case-browser';
import {
  AVAILABLE_USE_CASES,
  familyForPreset,
  useCaseById
} from '~/components/home/use-case-catalog';

export type CreationSource = 'preset' | 'prompt';

export const validateCreationSource = (
  target: TriggerTargetInput,
  source: CreationSource
): EmailErrors =>
  source === 'preset' && !target.presetId && !target.preset
    ? { target: 'Choose a preset or switch to Prompt explicitly.' }
    : {};

/** Revised design: one real workspace-run target; no fictitious action chain. */
export const CreationAction = ({
  issues,
  children
}: React.PropsWithChildren<{ issues: number }>) => (
  <div className="flex flex-col gap-y-6">
    {issues > 0 && (
      <div>
        <Badge color="red">{issues === 1 ? '1 to fix' : `${issues} to fix`}</Badge>
      </div>
    )}
    {children}
  </div>
);

export const CreationSourceChoice = ({
  value,
  onChange,
  disabled
}: {
  value: CreationSource;
  onChange: (source: CreationSource) => void;
  disabled?: boolean;
}) => {
  const id = React.useId();
  return (
    <div className="space-y-2">
      <span id={id} className="block text-sm/6 font-medium text-neutral-950 dark:text-white">
        Start workspace from
      </span>
      <SegmentedControl
        aria-labelledby={id}
        value={value}
        disabled={disabled}
        onChange={onChange}
        className="max-w-xs"
      >
        <SegmentedControlOption value="preset">Preset</SegmentedControlOption>
        <SegmentedControlOption value="prompt">Prompt</SegmentedControlOption>
      </SegmentedControl>
    </div>
  );
};

export const CreationThen = ({
  target,
  onChange,
  source,
  onSourceChange,
  privacy,
  onPrivacyChange,
  options,
  customPresets,
  hasProAccess,
  errors
}: {
  target: TriggerTargetInput;
  onChange: (target: TriggerTargetInput) => void;
  source: CreationSource;
  onSourceChange: (source: CreationSource) => void;
  privacy: Privacy;
  onPrivacyChange: (privacy: Privacy) => void;
  options: EmailOptions | null;
  customPresets: CustomPresetFieldsFragment[];
  hasProAccess: boolean;
  errors: EmailErrors;
}) => {
  const [browsing, setBrowsing] = React.useState(false);
  const allowedPresets = customPresets.filter(
    (preset) =>
      (preset.state === CustomPresetState.Ready ||
        preset.state === CustomPresetState.NeedsReview) &&
      options?.presets.some((option) => option.id === preset.id && option.ready)
  );
  const allowedUseCases = AVAILABLE_USE_CASES.filter(
    (useCase) =>
      !useCase.requiresLibrary &&
      options?.builtInPresets.some(
        (preset) => preset.preset === useCase.preset && preset.useCases.includes(useCase.id)
      )
  );
  const title = target.presetId
    ? (allowedPresets.find((preset) => preset.id === target.presetId)?.title ??
      'Unavailable preset')
    : target.preset
      ? (allowedUseCases.find(
          (useCase) => useCase.id === target.useCase && useCase.preset === target.preset
        )?.title ??
        (target.useCase
          ? 'Unavailable preset'
          : (familyForPreset(target.preset)?.title ?? 'Preset')))
      : null;
  const selectedUseCase = allowedUseCases.find(
    (useCase) => useCase.id === target.useCase && useCase.preset === target.preset
  );
  const chip = target.presetId ? (
    <CustomPresetChip />
  ) : selectedUseCase ? (
    <UseCaseChip useCase={selectedUseCase} />
  ) : undefined;
  const issues = Object.keys(errors).filter(
    (key) => key === 'privacy' || key === 'target' || key.startsWith('target.')
  ).length;
  const error =
    errors.target ?? Object.entries(errors).find(([key]) => key.startsWith('target.'))?.[1];
  const presetId = React.useId();
  const clear = () => onChange({ ...target, presetId: null, preset: null, useCase: null });
  return (
    <CreationAction issues={issues}>
      <CreationSourceChoice
        value={source}
        disabled={!options}
        onChange={(next) => {
          if (next === source) return;
          if (next === 'prompt') clear();
          onSourceChange(next);
        }}
      />
      {source === 'preset' && (
        <Field>
          <Label>Preset</Label>
          <Description id={`${presetId}-help`}>
            {!options
              ? 'Loading allowed presets…'
              : !allowedPresets.length && !allowedUseCases.length
                ? 'No ready, allowed presets are available. You can use prompt-only instructions instead.'
                : 'Choose an allowed built-in preset or a ready custom preset.'}
          </Description>
          <TaskSelection
            chip={chip}
            title={title}
            disabled={!options}
            onChoose={() => setBrowsing(true)}
            onRemove={clear}
          />
          {error && <ErrorMessage>{error}</ErrorMessage>}
        </Field>
      )}
      <div className="min-w-0">
        <BuilderField
          label="Workspace privacy"
          path="privacy"
          errors={errors}
          description={
            privacy === Privacy.MatchedMailboxOwners
              ? 'Only the owners of the mailboxes the email arrived in, and agency admins, can open this workspace.'
              : 'Everyone in this agency may access this workspace.'
          }
        >
          {(props) => (
            <Select
              {...props}
              className="sm:!max-w-xs"
              value={privacy}
              onChange={(event) => onPrivacyChange(event.target.value as Privacy)}
            >
              <option
                value={Privacy.MatchedMailboxOwners}
                disabled={options?.privacySupported === false}
              >
                Matched mailbox owners
              </option>
              <option value={Privacy.Agency}>Everyone in agency</option>
            </Select>
          )}
        </BuilderField>
        {options?.privacySupported === false && privacy === Privacy.MatchedMailboxOwners && (
          <Help role="alert" error>
            Matched-owner privacy is unavailable. Choose another policy explicitly; it will not be
            downgraded.
          </Help>
        )}
      </div>
      <div className="min-w-0">
        <BuilderField
          label={source === 'prompt' ? 'Prompt' : 'Additional instructions (optional)'}
          path="target.prompt"
          errors={errors}
          description={
            source === 'prompt'
              ? 'Required. Tell Cara what to do in the new workspace; no preset is used.'
              : 'Optional. Added to the chosen preset’s own instructions. Commas and line breaks are preserved.'
          }
        >
          {(props) => (
            <Textarea
              {...props}
              rows={3}
              value={target.prompt ?? ''}
              onChange={(event) => onChange({ ...target, prompt: event.target.value })}
            />
          )}
        </BuilderField>
        {source === 'prompt' && error && (
          <Help role="alert" error>
            {error}
          </Help>
        )}
      </div>
      <AnimatePresence initial mode="wait">
        {browsing && options && (
          <UseCaseBrowser
            key="email-creation-preset-browser"
            title="Choose a preset"
            useCases={allowedUseCases}
            customPresets={allowedPresets}
            hasProAccess={hasProAccess}
            onClose={() => setBrowsing(false)}
            onRun={(id) => {
              const useCase = useCaseById(id);
              if (
                !useCase ||
                !allowedUseCases.some((allowed) => allowed.id === id) ||
                (useCase.pro && !hasProAccess)
              )
                return;
              onChange({ ...target, presetId: null, preset: useCase.preset, useCase: id });
              setBrowsing(false);
            }}
            onRunCustomPreset={(id) => {
              if (!allowedPresets.some((preset) => preset.id === id)) return;
              onChange({ ...target, presetId: id, preset: null, useCase: null });
              setBrowsing(false);
            }}
          />
        )}
      </AnimatePresence>
    </CreationAction>
  );
};
